import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Paper,
  Typography,
  Chip,
  Snackbar,
  makeStyles,
  Tooltip,
  IconButton,
  Link,
  Badge,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import LockIcon from '@material-ui/icons/Lock';
import EditIcon from '@material-ui/icons/Edit';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import GitHubIcon from '@material-ui/icons/GitHub';
import BugReportIcon from '@material-ui/icons/BugReport';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import Editor, { OnMount, OnChange, Monaco } from '@monaco-editor/react';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { Progress, InfoCard } from '@backstage/core-components';
import { 
  yamlEditorApiRef, 
  type ValidateResponse, 
  type ValidationError, 
  type FieldInfo,
  type AiSuggestion,
  type AnalyzeAiResponse,
} from '../../api';
import { PrSubmissionDialog } from '../PrSubmissionDialog';
import { ErrorDetailsSidebar } from '../ErrorDetailsSidebar';
import { SchemaReferencePanel } from '../SchemaReferencePanel';
import { AiSuggestionsSidebar } from '../AiSuggestionsSidebar';
import { useAsyncRetry } from 'react-use';
import useMeasure from 'react-use-measure';
import * as YAML from 'yaml';

// Monaco editor types derived from OnMount callback
type MonacoEditor = Parameters<OnMount>[0];

const MIN_EDITOR_HEIGHT = 400;
const TOOLBAR_HEIGHT = 48;
const STATUS_BAR_HEIGHT = 28;

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: '100%',
    height: '100%',
    minHeight: MIN_EDITOR_HEIGHT + TOOLBAR_HEIGHT + STATUS_BAR_HEIGHT,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    flexShrink: 0,
    height: TOOLBAR_HEIGHT,
    boxSizing: 'border-box',
    width: '100%',
  },
  contentRow: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    minHeight: 0,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  editorContainer: {
    flex: 1,
    minHeight: MIN_EDITOR_HEIGHT,
    position: 'relative',
    overflow: 'hidden',
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    fontSize: '0.75rem',
    flexShrink: 0,
    height: STATUS_BAR_HEIGHT,
    boxSizing: 'border-box',
  },
  statusLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  validationStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  validIcon: {
    color: theme.palette.success.main,
    fontSize: 16,
  },
  invalidIcon: {
    color: theme.palette.error.main,
    fontSize: 16,
  },
  readOnlyChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  editableChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  changedIndicator: {
    marginLeft: theme.spacing(1),
    color: theme.palette.warning.main,
  },
  detailsButton: {
    marginLeft: theme.spacing(1),
  },
  errorBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
  },
  warningBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
  },
  aiBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  aiButtonLoading: {
    position: 'relative',
    '& .MuiCircularProgress-root': {
      position: 'absolute',
    },
  },
}));

export interface YamlEditorPageProps {
  entityRef: string;
  entityName: string;
  filePath?: string;
}

export const YamlEditorPage = ({
  entityRef,
  entityName,
  filePath,
}: YamlEditorPageProps) => {
  const classes = useStyles();
  const yamlEditorApi = useApi(yamlEditorApiRef);
  const githubAuthApi = useApi(githubAuthApiRef);
  
  // Measure wrapper for responsive height
  const [measureRef, bounds] = useMeasure();
  
  const [content, setContent] = useState<string>('');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [validation, setValidation] = useState<ValidateResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [prUrl, setPrUrl] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedError, setSelectedError] = useState<ValidationError | null>(null);
  const [schemaPanelOpen, setSchemaPanelOpen] = useState(false);
  const [selectedFieldPath, setSelectedFieldPath] = useState<string | undefined>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  // AI Analysis State
  const [aiSidebarOpen, setAiSidebarOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestion[]>([]);
  const [selectedAiSuggestion, setSelectedAiSuggestion] = useState<AiSuggestion | null>(null);
  const [isAnalyzingAi, setIsAnalyzingAi] = useState(false);
  const [aiAnalysisSummary, setAiAnalysisSummary] = useState<string>('');
  const [aiProcessingTime, setAiProcessingTime] = useState<number | undefined>();
  const [aiError, setAiError] = useState<string | undefined>();
  const [aiModels, setAiModels] = useState<string[]>([]);
  const [selectedAiModel, setSelectedAiModel] = useState<string>('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  const editorRef = useRef<MonacoEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const validationTimeoutRef = useRef<NodeJS.Timeout>();
  // Track AI suggestion decorations
  const aiDecorationIds = useRef<string[]>([]);
  // Track AI diff view zone IDs
  const aiViewZoneIds = useRef<string[]>([]);
  // Track inline diff decorations (for highlighting removed text)
  const aiDiffDecorationIds = useRef<string[]>([]);

  // Parse entity kind from YAML content
  const entityKind = useMemo(() => {
    if (!content) return undefined;
    try {
      const docs = YAML.parseAllDocuments(content);
      if (docs.length > 0) {
        const firstDoc = docs[0].toJS();
        return firstDoc?.kind as string | undefined;
      }
    } catch {
      // Ignore parse errors
    }
    return undefined;
  }, [content]);
  
  // Calculate editor height based on measured container
  const editorHeight = useMemo(() => {
    const availableHeight = bounds.height - TOOLBAR_HEIGHT - STATUS_BAR_HEIGHT;
    return Math.max(availableHeight, MIN_EDITOR_HEIGHT);
  }, [bounds.height]);

  // Fetch file content
  const {
    value: fileData,
    loading,
    error,
    retry,
  } = useAsyncRetry(async () => {
    const data = await yamlEditorApi.getFile(entityRef, filePath);
    setContent(data.content);
    setOriginalContent(data.content);
    setHasChanges(false);
    return data;
  }, [entityRef, filePath]);

  // Authenticate with GitHub for write access
  const handleAuthenticateGitHub = useCallback(async () => {
    try {
      // Request token with repo scope explicitly
      await githubAuthApi.getAccessToken(['repo']);
      // Retry fetching file with new credentials
      retry();
      setSnackbar({
        open: true,
        message: 'GitHub authenticated with repo scope! Refreshing permissions...',
        severity: 'success',
      });
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: `GitHub auth failed: ${e.message}. Make sure you grant 'repo' access when prompted.`,
        severity: 'error',
      });
    }
  }, [githubAuthApi, retry]);

  // Validate content with debounce
  const validateContent = useCallback(
    async (yamlContent: string) => {
      setIsValidating(true);
      try {
        const result = await yamlEditorApi.validateYaml(yamlContent);
        setValidation(result);
      } catch (e: any) {
        setValidation({
          valid: false,
          errors: [{ id: `err-${Date.now()}`, message: e.message || 'Validation failed', severity: 'error' }],
        });
      } finally {
        setIsValidating(false);
      }
    },
    [yamlEditorApi],
  );

  // Handle editor content change
  const handleEditorChange: OnChange = useCallback(
    (value) => {
      const newContent = value || '';
      setContent(newContent);
      setHasChanges(newContent !== originalContent);

      // Debounced validation
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      validationTimeoutRef.current = setTimeout(() => {
        validateContent(newContent);
      }, 500);
    },
    [originalContent, validateContent],
  );

  // Handle editor mount
  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
  }, []);

  // Update Monaco markers when validation changes
  useEffect(() => {
    if (!monacoRef.current || !editorRef.current) return undefined;

    const monaco = monacoRef.current;
    const model = editorRef.current.getModel();
    if (!model) return undefined;

    // Helper to map severity string to Monaco marker severity
    const getMarkerSeverity = (severity?: string) => {
      if (severity === 'warning') return monaco.MarkerSeverity.Warning;
      if (severity === 'info') return monaco.MarkerSeverity.Info;
      return monaco.MarkerSeverity.Error;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const markers: any[] = (validation?.errors || []).map(validationError => {
      const startLine = validationError.line || 1;
      const startColumn = validationError.column || 1;
      const endLine = validationError.endLine || startLine;
      const endColumn = validationError.endColumn || model.getLineMaxColumn(startLine);

      return {
        severity: getMarkerSeverity(validationError.severity),
        message: validationError.message + (validationError.details ? `\n\n${validationError.details}` : ''),
        startLineNumber: startLine,
        startColumn: startColumn,
        endLineNumber: endLine,
        endColumn: endColumn,
        source: 'yaml-editor',
        code: validationError.code,
      };
    });

    monaco.editor.setModelMarkers(model, 'yaml-editor', markers);

    // Cleanup on unmount
    return () => {
      if (model && !model.isDisposed()) {
        monaco.editor.setModelMarkers(model, 'yaml-editor', []);
      }
    };
  }, [validation]);

  // Handle error selection and navigation
  const handleSelectError = useCallback((selectedValidationError: ValidationError | null) => {
    setSelectedError(selectedValidationError);

    // Navigate to error line in editor (only if selecting, not deselecting)
    if (selectedValidationError?.line && editorRef.current) {
      editorRef.current.revealLineInCenter(selectedValidationError.line);
      editorRef.current.setPosition({
        lineNumber: selectedValidationError.line,
        column: selectedValidationError.column || 1,
      });
      editorRef.current.focus();
    }
  }, []);

  // Handle go to line from sidebar
  const handleGoToLine = useCallback((line: number, column?: number) => {
    if (editorRef.current) {
      editorRef.current.revealLineInCenter(line);
      editorRef.current.setPosition({
        lineNumber: line,
        column: column || 1,
      });
      editorRef.current.focus();
    }
  }, []);

  // Toggle error sidebar
  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  // Toggle schema reference panel
  const handleToggleSchemaPanel = useCallback(() => {
    setSchemaPanelOpen(prev => !prev);
  }, []);

  // Toggle AI suggestions sidebar
  const handleToggleAiSidebar = useCallback(() => {
    setAiSidebarOpen(prev => !prev);
  }, []);

  // Load available AI models
  const loadAiModels = useCallback(async () => {
    if (aiModels.length > 0) return; // Already loaded
    
    setIsLoadingModels(true);
    try {
      const result = await yamlEditorApi.getAiModels();
      setAiModels(result.models);
      // Set default model if not already set
      if (!selectedAiModel && result.models.length > 0) {
        // Prefer a smarter model if available
        const preferredModels = ['gemini-2.5-flash', 'gemini-2.0-flash', 'gpt-4o', 'gpt-4o-mini'];
        const defaultModel = preferredModels.find(m => 
          result.models.some(am => am.toLowerCase().includes(m.toLowerCase()))
        ) || result.models[0];
        setSelectedAiModel(defaultModel);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[YamlEditor] Failed to load AI models:', e);
      // Set fallback models
      setAiModels(['gpt-4o-mini', 'gpt-4o']);
      if (!selectedAiModel) {
        setSelectedAiModel('gpt-4o-mini');
      }
    } finally {
      setIsLoadingModels(false);
    }
  }, [yamlEditorApi, aiModels.length, selectedAiModel]);

  // Load models when sidebar opens
  useEffect(() => {
    if (aiSidebarOpen && aiModels.length === 0) {
      loadAiModels();
    }
  }, [aiSidebarOpen, aiModels.length, loadAiModels]);

  // AI Analysis
  const handleAnalyzeWithAi = useCallback(async () => {
    if (!content || isAnalyzingAi) return;

    setIsAnalyzingAi(true);
    setAiError(undefined);
    setAiSidebarOpen(true);

    // Load models if not already loaded
    if (aiModels.length === 0) {
      await loadAiModels();
    }

    try {
      // Gather validation errors to provide context
      const validationErrors = validation?.errors.map(err => ({
        message: err.message,
        path: err.path,
        line: err.line,
      }));

      const result: AnalyzeAiResponse = await yamlEditorApi.analyzeWithAi(
        content,
        entityRef,
        validationErrors,
        selectedAiModel || undefined,
      );

      setAiSuggestions(result.suggestions);
      setAiAnalysisSummary(result.summary);
      setAiProcessingTime(result.processingTime);
      setSelectedAiSuggestion(null);

      if (result.suggestions.length === 0) {
        setSnackbar({
          open: true,
          message: 'AI analysis complete - your YAML looks good!',
          severity: 'success',
        });
      }
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error('[YamlEditor] AI analysis failed:', e);
      setAiError(e.message || 'AI analysis failed');
      setSnackbar({
        open: true,
        message: `AI analysis failed: ${e.message}`,
        severity: 'error',
      });
    } finally {
      setIsAnalyzingAi(false);
    }
  }, [content, entityRef, validation, yamlEditorApi, isAnalyzingAi, selectedAiModel, aiModels.length, loadAiModels]);

  // Handle AI model change
  const handleAiModelChange = useCallback((model: string) => {
    setSelectedAiModel(model);
  }, []);

  // Handle AI suggestion selection
  const handleSelectAiSuggestion = useCallback((suggestion: AiSuggestion | null) => {
    setSelectedAiSuggestion(suggestion);
    
    // Navigate to suggestion line in editor
    if (suggestion?.line && editorRef.current) {
      editorRef.current.revealLineInCenter(suggestion.line);
      editorRef.current.setPosition({
        lineNumber: suggestion.line,
        column: 1,
      });
      editorRef.current.focus();
    }
  }, []);

  // Accept an AI suggestion - apply the change
  const handleAcceptAiSuggestion = useCallback((suggestion: AiSuggestion) => {
    if (!editorRef.current || !content) return;

    const lines = content.split('\n');
    let newContent = content;
    let applied = false;

    // Helper to extract the key from path - handles annotation keys with dots
    // e.g., "metadata.annotations.backstage.io/source-location" -> "backstage.io/source-location"
    // e.g., "metadata.labels.division" -> "division"
    const extractKeyFromPath = (path: string): string => {
      if (path.includes('annotations.')) {
        const annotationStart = path.indexOf('annotations.') + 'annotations.'.length;
        return path.substring(annotationStart);
      }
      if (path.includes('labels.')) {
        const labelStart = path.indexOf('labels.') + 'labels.'.length;
        return path.substring(labelStart);
      }
      const lastDot = path.lastIndexOf('.');
      return lastDot >= 0 ? path.substring(lastDot + 1) : path;
    };

    // Helper to find a line by key pattern (handles both quoted and unquoted keys)
    const findLineByKey = (key: string): number => {
      // Escape special regex chars in key (like dots and slashes)
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Match: optional quotes around key, then colon
      // Handles: key:, "key":, 'key':
      const keyPattern = new RegExp(`^\\s*["']?${escapedKey}["']?\\s*:`);
      
      for (let i = 0; i < lines.length; i++) {
        if (keyPattern.test(lines[i])) {
          return i;
        }
      }
      return -1;
    };

    // Helper to find line by current value (for cases where key search fails)
    const findLineByValue = (currentValue: string): number => {
      if (!currentValue) return -1;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes(currentValue)) {
          return i;
        }
      }
      return -1;
    };

    // Helper to find the end of a YAML section (annotations, labels, etc.)
    // Returns the index of the last line in that section
    const findSectionEnd = (sectionStartIndex: number, baseIndent: number): number => {
      let lastLineInSection = sectionStartIndex;
      for (let i = sectionStartIndex + 1; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) {
          // Empty line or comment - might still be in section
          continue;
        }
        const lineIndent = line.match(/^(\s*)/)?.[1].length || 0;
        if (lineIndent > baseIndent) {
          // This line is more indented, so it's part of the section
          lastLineInSection = i;
        } else {
          // Less or equal indent means we've exited the section
          break;
        }
      }
      return lastLineInSection;
    };

    // Helper to find a parent section (metadata, labels, annotations) and its indent
    const findSection = (sectionName: string): { index: number; indent: number; childIndent: string } | null => {
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();
        if (trimmed === `${sectionName}:` || trimmed.startsWith(`${sectionName}: `)) {
          const indent = line.length - line.trimStart().length;
          // Child indent is parent indent + 2 spaces
          const childIndent = ' '.repeat(indent + 2);
          return { index: i, indent, childIndent };
        }
      }
      return null;
    };

    // Handle different suggestion types
    if (suggestion.type === 'add' && suggestion.suggestedValue && suggestion.path) {
      const isAnnotation = suggestion.path.includes('annotations.');
      const isLabel = suggestion.path.includes('labels.');
      const newKey = extractKeyFromPath(suggestion.path);

      // Check if this key already exists (avoid duplicates)
      const existingLine = findLineByKey(newKey);
      if (existingLine >= 0) {
        // Key already exists - treat as modify instead
        const currentLine = lines[existingLine];
        const indent = currentLine.match(/^\s*/)?.[0] || '';
        lines[existingLine] = `${indent}${newKey}: ${suggestion.suggestedValue}`;
        newContent = lines.join('\n');
        applied = true;
      } else if (isAnnotation) {
        const annotationsSection = findSection('annotations');
        if (annotationsSection) {
          // Find the end of the annotations section to append there
          const sectionEnd = findSectionEnd(annotationsSection.index, annotationsSection.indent);
          const newLine = `${annotationsSection.childIndent}${newKey}: ${suggestion.suggestedValue}`;
          lines.splice(sectionEnd + 1, 0, newLine);
          newContent = lines.join('\n');
          applied = true;
        } else {
          // Annotations section doesn't exist - create it under metadata
          const metadataSection = findSection('metadata');
          if (metadataSection) {
            const metadataEnd = findSectionEnd(metadataSection.index, metadataSection.indent);
            const annotationsIndent = metadataSection.childIndent;
            const keyIndent = ' '.repeat(metadataSection.indent + 4);
            // Insert annotations section with the new key
            lines.splice(metadataEnd + 1, 0, 
              `${annotationsIndent}annotations:`,
              `${keyIndent}${newKey}: ${suggestion.suggestedValue}`
            );
            newContent = lines.join('\n');
            applied = true;
          }
        }
      } else if (isLabel) {
        const labelsSection = findSection('labels');
        if (labelsSection) {
          // Find the end of the labels section to append there
          const sectionEnd = findSectionEnd(labelsSection.index, labelsSection.indent);
          const newLine = `${labelsSection.childIndent}${newKey}: ${suggestion.suggestedValue}`;
          lines.splice(sectionEnd + 1, 0, newLine);
          newContent = lines.join('\n');
          applied = true;
        } else {
          // Labels section doesn't exist - create it under metadata (after name if possible)
          const metadataSection = findSection('metadata');
          if (metadataSection) {
            // Try to find a good insertion point - after name or at start of metadata
            let insertAfter = metadataSection.index;
            const nameIndex = findLineByKey('name');
            if (nameIndex >= 0 && nameIndex > metadataSection.index) {
              insertAfter = nameIndex;
            }
            const labelsIndent = metadataSection.childIndent;
            const keyIndent = ' '.repeat(metadataSection.indent + 4);
            // Insert labels section with the new key
            lines.splice(insertAfter + 1, 0, 
              `${labelsIndent}labels:`,
              `${keyIndent}${newKey}: ${suggestion.suggestedValue}`
            );
            newContent = lines.join('\n');
            applied = true;
          }
        }
      } else {
        // For other fields, find the parent section
        const pathParts = suggestion.path.split('.');
        const parentKey = pathParts.length > 1 ? pathParts[pathParts.length - 2] : '';
        const parentSection = parentKey ? findSection(parentKey) : null;

        if (parentSection) {
          const sectionEnd = findSectionEnd(parentSection.index, parentSection.indent);
          const newLine = `${parentSection.childIndent}${newKey}: ${suggestion.suggestedValue}`;
          lines.splice(sectionEnd + 1, 0, newLine);
          newContent = lines.join('\n');
          applied = true;
        }
      }
    } else if ((suggestion.type === 'modify' || suggestion.type === 'typo') && (suggestion.suggestedValue || suggestion.currentValue)) {
      // For modifications and typos, we need to handle two cases:
      // 1. KEY typo: the key itself is wrong (e.g., "kinddd" → "kind") - preserve the value
      // 2. VALUE modification: the value needs to change - preserve the key
      
      const correctKey = extractKeyFromPath(suggestion.path);
      let lineIndex = -1;
      let isKeyTypo = false;
      
      // First, try to find the line by the CORRECT key
      lineIndex = findLineByKey(correctKey);
      
      // If not found, this might be a KEY typo - search for the typo'd key
      if (lineIndex < 0 && suggestion.currentValue) {
        // For key typos, currentValue might contain the wrong key
        // Try to find a line that has the wrong key but right value structure
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const trimmed = line.trim();
          // Check if this line looks like it has a typo'd version of our key
          // by checking if currentValue appears in this line
          if (trimmed.includes(suggestion.currentValue) || 
              // Also check for common key typo patterns
              (trimmed.includes(':') && suggestion.path && 
               trimmed.toLowerCase().includes(suggestion.path.split('.').pop()?.toLowerCase().substring(0, 3) || ''))) {
            lineIndex = i;
            isKeyTypo = true;
            break;
          }
        }
      }
      
      // Still not found? Try more aggressive search
      if (lineIndex < 0 && suggestion.line && suggestion.line > 0 && suggestion.line <= lines.length) {
        // Use the suggested line number as a hint
        lineIndex = suggestion.line - 1;
        const lineContent = lines[lineIndex]?.trim() || '';
        // Only use this line if it looks like a YAML key-value pair
        if (lineContent.includes(':')) {
          isKeyTypo = !lineContent.startsWith(`${correctKey}:`) && !lineContent.startsWith(`"${correctKey}":`);
        }
      }
      
      if (lineIndex >= 0) {
        const currentLine = lines[lineIndex];
        const indent = currentLine.match(/^\s*/)?.[0] || '';
        
        // Parse the current line to extract its value
        const colonIndex = currentLine.indexOf(':');
        let existingValue = '';
        if (colonIndex >= 0) {
          existingValue = currentLine.substring(colonIndex + 1).trim();
        }
        
        // Determine the final value to use
        let finalValue: string;
        if (isKeyTypo || suggestion.type === 'typo') {
          // For key typos, preserve the existing value unless suggestedValue explicitly provides one
          // Check if suggestedValue looks like a value (not just the key name repeated)
          if (suggestion.suggestedValue && 
              suggestion.suggestedValue !== correctKey && 
              suggestion.suggestedValue !== suggestion.path.split('.').pop()) {
            finalValue = suggestion.suggestedValue;
          } else {
            finalValue = existingValue || suggestion.currentValue || '';
          }
        } else {
          // For value modifications, use the suggested value
          finalValue = suggestion.suggestedValue || existingValue;
        }
        
        // Check if the key needs quoting (contains special chars)
        const needsQuotes = correctKey.includes('/') || correctKey.includes('.') || correctKey.includes(':');
        const quotedKey = needsQuotes ? `"${correctKey}"` : correctKey;
        
        lines[lineIndex] = `${indent}${quotedKey}: ${finalValue}`;
        newContent = lines.join('\n');
        applied = true;
      }
    } else if (suggestion.type === 'remove') {
      // For removals, find line by key pattern (not by line number)
      const key = extractKeyFromPath(suggestion.path);
      let lineIndex = findLineByKey(key);
      
      // Fallback: try to find by current value
      if (lineIndex < 0 && suggestion.currentValue) {
        lineIndex = findLineByValue(suggestion.currentValue);
      }
      
      if (lineIndex >= 0) {
        lines.splice(lineIndex, 1);
        newContent = lines.join('\n');
        applied = true;
      }
    }

    if (applied) {
      setContent(newContent);
      setHasChanges(newContent !== originalContent);
      
      // Remove from suggestions list
      setAiSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      setSelectedAiSuggestion(null);
      
      setSnackbar({
        open: true,
        message: `Applied: ${suggestion.title}`,
        severity: 'success',
      });

      // Trigger re-validation
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      validationTimeoutRef.current = setTimeout(() => {
        validateContent(newContent);
      }, 500);
    } else {
      setSnackbar({
        open: true,
        message: 'Could not apply suggestion automatically. Please edit manually.',
        severity: 'warning',
      });
    }
  }, [content, originalContent, validateContent]);

  // Reject/dismiss an AI suggestion
  const handleRejectAiSuggestion = useCallback((suggestion: AiSuggestion) => {
    setAiSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    setSelectedAiSuggestion(null);
  }, []);

  // Helper function to escape HTML
  const escapeHtml = (text: string) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Update Monaco decorations for AI suggestions
  useEffect(() => {
    if (!monacoRef.current || !editorRef.current) return;

    const monaco = monacoRef.current;
    const editor = editorRef.current;
    const model = editor.getModel();
    if (!model) return;

    // Clear previous decorations
    if (aiDecorationIds.current.length > 0) {
      editor.deltaDecorations(aiDecorationIds.current, []);
      aiDecorationIds.current = [];
    }

    // Clear previous diff decorations
    if (aiDiffDecorationIds.current.length > 0) {
      editor.deltaDecorations(aiDiffDecorationIds.current, []);
      aiDiffDecorationIds.current = [];
    }

    // Clear previous view zones
    if (aiViewZoneIds.current.length > 0) {
      editor.changeViewZones((accessor: any) => {
        aiViewZoneIds.current.forEach(id => accessor.removeZone(id));
      });
      aiViewZoneIds.current = [];
    }

    // Add new decorations for AI suggestions
    if (aiSuggestions.length > 0 && aiSidebarOpen) {
      const decorations = aiSuggestions.map(suggestion => {
        const isSelected = selectedAiSuggestion?.id === suggestion.id;
        
        // Determine decoration class based on type
        let className = 'ai-suggestion-line';
        let glyphClassName = 'ai-suggestion-glyph';
        
        if (suggestion.type === 'add') {
          className = 'ai-suggestion-line-add';
          glyphClassName = 'ai-suggestion-glyph-add';
        } else if (suggestion.type === 'typo') {
          className = 'ai-suggestion-line-typo';
          glyphClassName = 'ai-suggestion-glyph-typo';
        } else if (suggestion.type === 'remove') {
          className = 'ai-suggestion-line-remove';
          glyphClassName = 'ai-suggestion-glyph-remove';
        }

        if (isSelected) {
          className += ' ai-suggestion-selected';
        }

        return {
          range: new monaco.Range(
            suggestion.line,
            1,
            suggestion.endLine || suggestion.line,
            model.getLineMaxColumn(suggestion.endLine || suggestion.line),
          ),
          options: {
            isWholeLine: true,
            className,
            glyphMarginClassName: glyphClassName,
            glyphMarginHoverMessage: { value: `**${suggestion.title}**\n\n${suggestion.description}` },
            hoverMessage: { value: `**AI Suggestion:** ${suggestion.title}\n\n${suggestion.description}` },
          },
        };
      });

      aiDecorationIds.current = editor.deltaDecorations([], decorations);

      // Add inline diff preview for selected suggestion
      if (selectedAiSuggestion) {
        const suggestion = selectedAiSuggestion;
        
        // Helper to extract the key from path for annotations/labels
        const extractKeyFromPath = (path: string): string => {
          if (path.includes('annotations.')) {
            const annotationStart = path.indexOf('annotations.') + 'annotations.'.length;
            return path.substring(annotationStart);
          }
          if (path.includes('labels.')) {
            const labelStart = path.indexOf('labels.') + 'labels.'.length;
            return path.substring(labelStart);
          }
          const lastDot = path.lastIndexOf('.');
          return lastDot >= 0 ? path.substring(lastDot + 1) : path;
        };
        
        const key = extractKeyFromPath(suggestion.path);
        // Check if the key needs quoting (contains special chars)
        const needsQuotes = key.includes('/') || key.includes('.') || key.includes(':');
        const displayKey = needsQuotes ? `"${key}"` : key;
        const suggestedLine = suggestion.suggestedValue 
          ? `${displayKey}: ${suggestion.suggestedValue}`
          : displayKey;
        
        // Create the diff preview view zone
        editor.changeViewZones((accessor: any) => {
          const domNode = document.createElement('div');
          domNode.className = 'ai-diff-preview-container';
          
          // Build diff content
          let diffHtml = '';
          
          if (suggestion.type === 'add') {
            // Show the line to be added (green)
            diffHtml = `
              <div class="ai-diff-preview">
                <div class="ai-diff-header">
                  <span class="ai-diff-label ai-diff-label-add">+ ADD</span>
                  <span class="ai-diff-title">${escapeHtml(suggestion.title)}</span>
                </div>
                <div class="ai-diff-line ai-diff-line-add">
                  <span class="ai-diff-line-prefix">+</span>
                  <span class="ai-diff-line-content">${escapeHtml(suggestedLine)}</span>
                </div>
              </div>
            `;
          } else if (suggestion.type === 'modify' || suggestion.type === 'typo') {
            // Show before (red) and after (green)
            // Try to get the current line from the editor
            let currentLine = '';
            if (suggestion.line > 0 && suggestion.line <= model.getLineCount()) {
              currentLine = model.getLineContent(suggestion.line).trim();
            }
            // Fall back to currentValue if we have it
            if (!currentLine && suggestion.currentValue) {
              currentLine = `${displayKey}: ${suggestion.currentValue}`;
            }
            
            diffHtml = `
              <div class="ai-diff-preview">
                <div class="ai-diff-header">
                  <span class="ai-diff-label ai-diff-label-modify">${suggestion.type === 'typo' ? '✏ TYPO FIX' : '✎ MODIFY'}</span>
                  <span class="ai-diff-title">${escapeHtml(suggestion.title)}</span>
                </div>
                ${currentLine ? `
                <div class="ai-diff-line ai-diff-line-remove">
                  <span class="ai-diff-line-prefix">−</span>
                  <span class="ai-diff-line-content">${escapeHtml(currentLine)}</span>
                </div>
                ` : ''}
                ${suggestion.suggestedValue ? `
                <div class="ai-diff-line ai-diff-line-add">
                  <span class="ai-diff-line-prefix">+</span>
                  <span class="ai-diff-line-content">${escapeHtml(suggestedLine)}</span>
                </div>
                ` : ''}
              </div>
            `;
          } else if (suggestion.type === 'remove') {
            // Show the line to be removed (red)
            let currentLine = '';
            if (suggestion.line > 0 && suggestion.line <= model.getLineCount()) {
              currentLine = model.getLineContent(suggestion.line).trim();
            }
            if (!currentLine && suggestion.currentValue) {
              currentLine = `${displayKey}: ${suggestion.currentValue}`;
            }
            
            diffHtml = `
              <div class="ai-diff-preview">
                <div class="ai-diff-header">
                  <span class="ai-diff-label ai-diff-label-remove">✕ REMOVE</span>
                  <span class="ai-diff-title">${escapeHtml(suggestion.title)}</span>
                </div>
                ${currentLine ? `
                <div class="ai-diff-line ai-diff-line-remove">
                  <span class="ai-diff-line-prefix">−</span>
                  <span class="ai-diff-line-content">${escapeHtml(currentLine)}</span>
                </div>
                ` : ''}
              </div>
            `;
          }
          
          if (diffHtml) {
            domNode.innerHTML = diffHtml;
            
            // Calculate height based on content
            const lineCount = (diffHtml.match(/ai-diff-line-/g) || []).length;
            const heightInLines = Math.max(2, lineCount + 1.5);
            
            const viewZoneId = accessor.addZone({
              afterLineNumber: suggestion.line,
              heightInLines,
              domNode,
              suppressMouseDown: true,
            });
            
            aiViewZoneIds.current.push(viewZoneId);
          }
        });
      }
    }
  }, [aiSuggestions, aiSidebarOpen, selectedAiSuggestion]);

  // Inject CSS for AI suggestion decorations
  useEffect(() => {
    const styleId = 'ai-suggestion-styles';
    if (document.getElementById(styleId)) return undefined;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .ai-suggestion-line {
        background-color: rgba(33, 150, 243, 0.1) !important;
      }
      .ai-suggestion-line-add {
        background-color: rgba(76, 175, 80, 0.1) !important;
      }
      .ai-suggestion-line-typo {
        background-color: rgba(156, 39, 176, 0.1) !important;
      }
      .ai-suggestion-line-remove {
        background-color: rgba(244, 67, 54, 0.1) !important;
      }
      .ai-suggestion-selected {
        background-color: rgba(33, 150, 243, 0.25) !important;
        border-left: 3px solid #2196f3 !important;
      }
      .ai-suggestion-glyph {
        background-color: #2196f3;
        border-radius: 50%;
        width: 8px !important;
        height: 8px !important;
        margin-left: 4px;
        margin-top: 6px;
      }
      .ai-suggestion-glyph-add {
        background-color: #4caf50;
        border-radius: 50%;
        width: 8px !important;
        height: 8px !important;
        margin-left: 4px;
        margin-top: 6px;
      }
      .ai-suggestion-glyph-typo {
        background-color: #9c27b0;
        border-radius: 50%;
        width: 8px !important;
        height: 8px !important;
        margin-left: 4px;
        margin-top: 6px;
      }
      .ai-suggestion-glyph-remove {
        background-color: #f44336;
        border-radius: 50%;
        width: 8px !important;
        height: 8px !important;
        margin-left: 4px;
        margin-top: 6px;
      }
      
      /* Inline diff preview styles - works with vs-dark theme */
      .ai-diff-preview-container {
        padding: 4px 8px 4px 62px;
        font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
        font-size: 13px;
        line-height: 1.5;
      }
      .ai-diff-preview {
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 6px;
        overflow: hidden;
        margin: 2px 0;
        background-color: rgba(30, 30, 30, 0.95);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }
      .ai-diff-header {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 10px;
        background-color: rgba(60, 60, 60, 0.9);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      .ai-diff-label {
        font-size: 10px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 4px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .ai-diff-label-add {
        background-color: rgba(76, 175, 80, 0.4);
        color: #a5d6a7;
        border: 1px solid rgba(76, 175, 80, 0.6);
      }
      .ai-diff-label-modify {
        background-color: rgba(255, 152, 0, 0.4);
        color: #ffcc80;
        border: 1px solid rgba(255, 152, 0, 0.6);
      }
      .ai-diff-label-remove {
        background-color: rgba(244, 67, 54, 0.4);
        color: #ef9a9a;
        border: 1px solid rgba(244, 67, 54, 0.6);
      }
      .ai-diff-title {
        font-size: 11px;
        color: #b0b0b0;
        font-weight: 500;
      }
      .ai-diff-line {
        display: flex;
        align-items: stretch;
        min-height: 22px;
        line-height: 22px;
      }
      .ai-diff-line-add {
        background: linear-gradient(90deg, rgba(76, 175, 80, 0.25) 0%, rgba(76, 175, 80, 0.15) 100%);
        border-left: 3px solid #4caf50;
      }
      .ai-diff-line-remove {
        background: linear-gradient(90deg, rgba(244, 67, 54, 0.25) 0%, rgba(244, 67, 54, 0.15) 100%);
        border-left: 3px solid #f44336;
      }
      .ai-diff-line-prefix {
        width: 24px;
        flex-shrink: 0;
        font-weight: 700;
        text-align: center;
        font-size: 14px;
      }
      .ai-diff-line-add .ai-diff-line-prefix {
        color: #66bb6a;
      }
      .ai-diff-line-remove .ai-diff-line-prefix {
        color: #ef5350;
      }
      .ai-diff-line-content {
        flex: 1;
        white-space: pre;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: 10px;
      }
      .ai-diff-line-add .ai-diff-line-content {
        color: #a5d6a7;
      }
      .ai-diff-line-remove .ai-diff-line-content {
        color: #ef9a9a;
        text-decoration: line-through;
        text-decoration-color: rgba(239, 154, 154, 0.6);
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Handle field click from schema panel - navigate to field in editor
  const handleSchemaFieldClick = useCallback((field: FieldInfo) => {
    setSelectedFieldPath(field.path);
    
    // Try to find the field in the YAML and navigate to it
    if (editorRef.current && content) {
      const lines = content.split('\n');
      const fieldKey = field.name;
      
      // Simple search for the field - look for the key at the start of a line
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trimStart();
        if (trimmed.startsWith(`${fieldKey}:`) || trimmed.startsWith(`${fieldKey} :`)) {
          const lineNumber = i + 1;
          const column = line.indexOf(fieldKey) + 1;
          editorRef.current.revealLineInCenter(lineNumber);
          editorRef.current.setPosition({ lineNumber, column });
          editorRef.current.focus();
          break;
        }
      }
    }
  }, [content]);

  // Validate on initial load
  useEffect(() => {
    if (content && !validation) {
      validateContent(content);
    }
  }, [content, validation, validateContent]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (hasChanges) {
      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          'You have unsaved changes. Are you sure you want to refresh?',
        )
      ) {
        return;
      }
    }
    retry();
  }, [hasChanges, retry]);

  // Handle save (open PR dialog)
  const handleSave = useCallback(() => {
    if (!validation?.valid) {
      setSnackbar({
        open: true,
        message: 'Please fix validation errors before saving',
        severity: 'error',
      });
      return;
    }
    setPrDialogOpen(true);
    setPrUrl(undefined);
  }, [validation]);

  // Handle PR submission
  const handleSubmitPr = useCallback(
    async (title: string, description: string) => {
      setIsSubmitting(true);
      try {
        const result = await yamlEditorApi.submitPullRequest({
          entityRef,
          content,
          title,
          description,
          filePath,
        });
        setPrUrl(result.prUrl);
        setSnackbar({
          open: true,
          message: 'Pull request created successfully!',
          severity: 'success',
        });
        // Reset change tracking after successful PR
        setOriginalContent(content);
        setHasChanges(false);
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.error('[YamlEditor] PR submission failed:', e);
        
        // Determine if it's an auth error that might be fixable
        const isAuthError = e.message?.toLowerCase().includes('auth') || 
                           e.message?.toLowerCase().includes('token') ||
                           e.message?.toLowerCase().includes('401');
        
        setSnackbar({
          open: true,
          message: isAuthError 
            ? `${e.message} Try clicking "Authenticate" to refresh your GitHub access.`
            : (e.message || 'Failed to create pull request'),
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [yamlEditorApi, entityRef, content, filePath],
  );

  // Handle snackbar close
  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <InfoCard title="Error">
        <Typography color="error">{error.message}</Typography>
        <Button
          variant="outlined"
          onClick={retry}
          startIcon={<RefreshIcon />}
          style={{ marginTop: 16 }}
        >
          Retry
        </Button>
      </InfoCard>
    );
  }

  const canEdit = fileData?.canEdit ?? false;
  const errorCount = validation?.errors.filter(e => e.severity === 'error' || !e.severity).length || 0;
  const warningCount = validation?.errors.filter(e => e.severity === 'warning').length || 0;
  let aiButtonTooltip = 'Analyze with AI';
  if (isAnalyzingAi) aiButtonTooltip = 'Analyzing...';
  else if (aiSidebarOpen) aiButtonTooltip = 'Hide AI suggestions';

  return (
    <div ref={measureRef} className={classes.wrapper}>
      {/* Toolbar - stays full width regardless of sidebars */}
      <div className={classes.toolbar}>
        <div className={classes.toolbarLeft}>
          <Typography variant="subtitle2">
            {fileData?.path || 'catalog-info.yaml'}
            {hasChanges && (
              <span className={classes.changedIndicator}>*</span>
            )}
          </Typography>
          {canEdit ? (
            <Chip
              size="small"
              icon={<EditIcon />}
              label="Editable"
              className={classes.editableChip}
            />
          ) : (
            <Tooltip title="Read-only: You need GitHub write access. Click 'Authenticate' and ensure the OAuth app is authorized for this org in GitHub Settings → Applications.">
              <Chip
                size="small"
                icon={<LockIcon />}
                label="Read Only"
                className={classes.readOnlyChip}
              />
            </Tooltip>
          )}
        </div>
        <div className={classes.toolbarRight}>
              {/* AI Analysis Button */}
              <Tooltip title={aiButtonTooltip}>
                <IconButton 
                  size="small" 
                  onClick={aiSidebarOpen ? handleToggleAiSidebar : handleAnalyzeWithAi}
                  color={aiSidebarOpen ? 'primary' : 'default'}
                  disabled={isAnalyzingAi && !aiSidebarOpen}
                  className={isAnalyzingAi ? classes.aiButtonLoading : undefined}
                >
                  <Badge 
                    badgeContent={aiSuggestions.length} 
                    className={classes.aiBadge}
                    invisible={aiSuggestions.length === 0 || isAnalyzingAi}
                  >
                    {isAnalyzingAi ? (
                      <CircularProgress size={20} color="primary" />
                    ) : (
                      <EmojiObjectsIcon />
                    )}
                  </Badge>
                </IconButton>
              </Tooltip>
              {/* Schema Reference Button */}
              <Tooltip title={schemaPanelOpen ? 'Hide schema reference' : 'Show schema reference'}>
                <IconButton 
                  size="small" 
                  onClick={handleToggleSchemaPanel}
                  color={schemaPanelOpen ? 'primary' : 'default'}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              {/* Validation Details Button */}
              <Tooltip title={sidebarOpen ? 'Hide validation details' : 'Show validation details'}>
                <IconButton 
                  size="small" 
                  onClick={handleToggleSidebar}
                  color={sidebarOpen ? 'primary' : 'default'}
                >
                  <Badge 
                    badgeContent={errorCount + warningCount} 
                    className={errorCount > 0 ? classes.errorBadge : classes.warningBadge}
                    invisible={errorCount + warningCount === 0}
                  >
                    <BugReportIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh from repository">
                <IconButton size="small" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {!canEdit && (
                <Tooltip title="Sign in with GitHub to enable editing">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GitHubIcon />}
                    onClick={handleAuthenticateGitHub}
                  >
                    Authenticate
                  </Button>
                </Tooltip>
              )}
              {canEdit && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={!hasChanges || !validation?.valid}
                >
                  Save Changes
                </Button>
              )}
            </div>
          </div>

      {/* Content Row - Editor + Sidebars (below toolbar) */}
      <div className={classes.contentRow}>
        <Paper className={classes.mainContent} elevation={0}>
          <div className={classes.container}>
          {/* Monaco Editor */}
          <div className={classes.editorContainer} style={{ height: editorHeight }}>
            <Editor
              height={editorHeight}
              width="100%"
              defaultLanguage="yaml"
              value={content}
              onChange={handleEditorChange}
              onMount={handleEditorMount}
              options={{
                readOnly: !canEdit,
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                tabSize: 2,
                insertSpaces: true,
                automaticLayout: true,
                folding: true,
                renderLineHighlight: 'all',
                glyphMargin: true,
                // Allow hover widgets to escape editor bounds and position intelligently
                fixedOverflowWidgets: true,
                hover: {
                  above: false, // Prefer showing hover below the line to avoid top-of-viewport issues
                },
              }}
              theme="vs-dark"
            />
          </div>

          {/* Status Bar */}
          <div className={classes.statusBar}>
            <div className={classes.statusLeft}>
              <Tooltip title="Click to toggle validation panel">
                <div 
                  className={classes.validationStatus}
                  onClick={handleToggleSidebar}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleToggleSidebar();
                  }}
                >
                  {isValidating && (
                    <Typography variant="caption" color="textSecondary">
                      Validating...
                    </Typography>
                  )}
                  {!isValidating && validation?.valid && (
                    <>
                      <CheckCircleIcon className={classes.validIcon} />
                      <Typography variant="caption">Valid YAML</Typography>
                    </>
                  )}
                  {!isValidating && !validation?.valid && (
                    <>
                      <ErrorIcon className={classes.invalidIcon} />
                      <Typography variant="caption" color="error">
                        {errorCount} error{errorCount !== 1 ? 's' : ''}
                        {warningCount > 0 && `, ${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
                      </Typography>
                    </>
                  )}
                </div>
              </Tooltip>
            </div>
            {fileData?.repositoryUrl && (
              <Link
                href={fileData.repositoryUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                color="textSecondary"
              >
                {fileData.repositoryUrl}
              </Link>
            )}
          </div>

          {/* PR Submission Dialog */}
          <PrSubmissionDialog
            open={prDialogOpen}
            onClose={() => setPrDialogOpen(false)}
            onSubmit={handleSubmitPr}
            entityName={entityName}
            prUrl={prUrl}
            isSubmitting={isSubmitting}
          />

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
          </div>
        </Paper>

        {/* Schema Reference Panel */}
      {schemaPanelOpen && (
        <SchemaReferencePanel
          kind={entityKind}
          errors={validation?.errors}
          onFieldClick={handleSchemaFieldClick}
          selectedFieldPath={selectedFieldPath}
          onClose={() => setSchemaPanelOpen(false)}
          content={content}
        />
      )}

      {/* Error Details Sidebar */}
      <ErrorDetailsSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        errors={validation?.errors || []}
        selectedError={selectedError}
        onSelectError={handleSelectError}
        onGoToLine={handleGoToLine}
        entityKind={entityKind}
      />

      {/* AI Suggestions Sidebar */}
      <AiSuggestionsSidebar
        open={aiSidebarOpen}
        onClose={() => setAiSidebarOpen(false)}
        suggestions={aiSuggestions}
        selectedSuggestion={selectedAiSuggestion}
        onSelectSuggestion={handleSelectAiSuggestion}
        onAcceptSuggestion={handleAcceptAiSuggestion}
        onRejectSuggestion={handleRejectAiSuggestion}
        onGoToLine={handleGoToLine}
        onRefresh={handleAnalyzeWithAi}
        isLoading={isAnalyzingAi}
        summary={aiAnalysisSummary}
        processingTime={aiProcessingTime}
        error={aiError}
        availableModels={aiModels}
        selectedModel={selectedAiModel}
        onModelChange={handleAiModelChange}
        isLoadingModels={isLoadingModels}
      />
      </div> {/* End contentRow */}
    </div>
  );
};

